import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  ErrorDisplayRemoveRelation,
  ErrorDisplayUnsavedItems,
} from "@ryde/controls";
import {
  useConfirmDirtyField,
  useCrudEditContext,
  useCrudFormCleanup,
  useCrudNavigate,
  useCrudSave,
  useDynamicFormsFetch,
  useModalConfirm,
  useTableListActions,
} from "@ryde/redux";
import {
  canResolveAlways,
  ConfirmResult,
  createSuccessResult,
  EditFormMode,
  errorsCrudContentAtomFamily,
  IExtendableDto,
  isBookingEntity,
  isBookingErrors,
  Log,
  markKeepEditFormDtoOnUnmount,
  RydeAppUnit,
  SimpleResult,
  SINGLE_KEY,
  TCanResolve,
  TDrawerCloseFunction,
  TResolveFn,
} from "@ryde/shared";

export const useEditFormInner = <T extends IExtendableDto>({
  resolveFn,
  onCloseDrawer,
  canResolve = canResolveAlways,
}: {
  onCloseDrawer: TDrawerCloseFunction;
  resolveFn?: TResolveFn;
  canResolve?: TCanResolve;
}) => {
  const {
    data: { isEdit, archive, archiveWithRelation, dto, processDuplicationDto },
    entity,
    mode,
  } = useCrudEditContext<T>();

  const { navigateToAddOrDuplicate } = useCrudNavigate(entity as RydeAppUnit);
  const { t } = useTranslation();
  const { addTableDto, replaceTableDto } = useTableListActions(entity);
  const { resetEditFormPreviousData } = useCrudFormCleanup(entity, mode);
  const setCrudServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily(entity),
  );

  const isAddNew = !isEdit;

  const {
    setError,
    formState: { isDirty },
  } = useFormContext();
  const { saveCrudEntity } = useCrudSave<T>();

  const { fetchTableRows } = useDynamicFormsFetch(entity);

  const { confirmDialogResult } = useModalConfirm();

  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems />,
  });

  const closeSidebarAndFetch = (): void => {
    fetchTableRows?.();
    closeSidebar?.();
  };

  const closeSidebar = useEvent(
    applyIfConfirmed({ isDirty })(() => {
      onCloseDrawer();
    }),
  );

  const closeSidebarOk = useEvent((savedDto: IExtendableDto | undefined) => {
    if (canResolve(savedDto)) {
      resolveFn?.(createSuccessResult(savedDto));
    } else {
      Log.error(t("errors.invalidDTOFromDropdown"), savedDto);
    }

    if (mode === EditFormMode.CrudRoute) {
      if (savedDto) {
        if (isAddNew) {
          addTableDto(savedDto);
        } else {
          replaceTableDto(savedDto);
        }
      }
    } else {
      resetEditFormPreviousData();
    }

    onCloseDrawer();
  });

  const handleBookingSaveResult = useEvent(
    (saveResult: SimpleResult<IExtendableDto>) => {
      if (saveResult?.success && isBookingEntity(saveResult?.data)) {
        closeSidebarOk(saveResult.data);
      } else if (isBookingErrors(saveResult.data)) {
        setCrudServerErrors(saveResult.data.errors);
      } else if (saveResult.errors?.length) {
        for (const error of saveResult.errors) {
          if (error.path) {
            setError(error.path, error);
          }
        }
      }
    },
  );

  const handleDefaultSaveResult = useEvent(
    (saveResult: SimpleResult<IExtendableDto>) => {
      if (saveResult?.success && saveResult.data) {
        closeSidebarOk(saveResult.data);
      } else if (saveResult.errors?.length) {
        for (const error of saveResult.errors) {
          if (error.path) {
            if (error.translationKey) {
              error.message = t(
                error.translationKey,
                error.translationParams ?? {},
              );
            }

            setError(error.path, error);
          }
        }
      }
    },
  );

  const onSaveClick = useEvent(async () => {
    try {
      const saveResult = await saveCrudEntity(dto);

      if (entity === "booking") {
        handleBookingSaveResult(saveResult);
      } else {
        handleDefaultSaveResult(saveResult);
      }
    } catch (e: unknown) {
      Log.warnAndToast(t("errors.ERROR_SAVING_DATA"), e);
    }
  });

  const onArchiveClick = useEvent(async () => {
    setCrudServerErrors(undefined);

    if (isAddNew) {
      Log.warnAndToast(t("errors.errorInvalidEntityForArchiving"));
      return;
    }

    if (!archive) {
      Log.warnAndToast(
        t("errors.invalidMethodSkipping", { methodName: t("general.ARCHIVE") }),
      );
      return;
    }

    try {
      const archiveResult = await archive();

      if (archiveResult?.success) {
        closeSidebarAndFetch();
        return;
      }

      if (archiveResult?.data?.isDeleteRelationConfirm) {
        const confirmationResult = await confirmDialogResult({
          customContent: (
            <ErrorDisplayRemoveRelation
              relationWarningMessage={archiveResult?.data?.errorMessage}
            />
          ),
          title: t("general.DELETE_ENTITY_CONFIRMATION", {
            entity: t(`${entity}.${SINGLE_KEY}`),
          }),
        });

        if (confirmationResult === ConfirmResult.OK) {
          const archiveRelationResult = await archiveWithRelation?.();

          if (archiveRelationResult?.success) {
            closeSidebarAndFetch();
          }
        }
      }
    } catch (e: unknown) {
      Log.error(
        t("errors.errorArchivingEntityData", {
          entity: t(`${entity}.${SINGLE_KEY}`),
        }),
        e,
      );
    }
  });

  const onDuplicateClick = useEvent(async () => {
    if (isAddNew) {
      Log.warnAndToast(t("errors.errorInvalidEntityIdForDuplication"));
      return;
    }

    const duplicatedDto = processDuplicationDto(dto);
    if (mode === EditFormMode.CrudRoute) {
      markKeepEditFormDtoOnUnmount([entity, mode]);
    }
    navigateToAddOrDuplicate(dto?.id, true, duplicatedDto);
  });

  // DO NOT REMOVE!. Demand may return.
  const subHeaderTitle = "";

  return {
    onDuplicateClick,
    onArchiveClick,
    onSaveClick,
    closeSidebar,
    subHeaderTitle,
    closeSidebarOk,
  };
};
